import React, { useEffect, useState } from "react";
import { apiFetch } from "../utils/wkCommon";
import LookupSong from "./LookupSong";
import { getUser } from "../utils/auth";

const user = getUser();

export default function PlaylistItemForm({
  playlist,
  onClose,
  onSaved
}) {

  const [items, setItems] = useState([]);
  const [selectedSong, setSelectedSong] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [toast, setToast] = useState(null);

  // =========================
  // STYLE
  // =========================
  const btnSmall = {
    padding: "0.25rem 0.5rem",
    fontSize: "0.75rem",
    borderRadius: "0.5rem"
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.5rem",
    padding: "0.75rem 0",
    borderBottom: "1px solid #eee"
  };

  const inputStyle = {
    width: "100%",
    padding: "0.625rem 0.875rem",
    borderRadius: "0.625rem",
    border: "1px solid #ddd",
    fontSize: "0.875rem"
  };

  // =========================
  // LOAD
  // =========================
  async function load() {
    const params = new URLSearchParams({
      "filters[song_playlist][documentId][$eq]": playlist.documentId,
      "sort[0]": "orderNo:asc",
      "populate[0]": "virtuous_song"
    });

    const json = await apiFetch(`/api/song-playlist-items?${params}`);
    setItems(json?.data || []);
  }

  useEffect(() => {
    load();
  }, []);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 1500);
  }

  // =========================
  // ADD (BUTTON)
  // =========================
  async function handleAdd() {
    if (!selectedSong) return;

    const docId = selectedSong.documentId || selectedSong.id;

    // prevent duplicate
    if (items.some(i => i.virtuous_song?.documentId === docId)) {
      showToast("Already in playlist");
      return;
    }

    const tempId = "temp-" + Date.now();

    const newItem = {
      id: tempId,
      documentId: tempId,
      virtuous_song: selectedSong
    };

    // tampil langsung
    setItems(prev => [...prev, newItem]);
    setSelectedSong(null);

    showToast("Song added");
    onSaved();

    try {
      await apiFetch(`/api/song-playlist-items`, {
        method: "POST",
        body: {
          data: {
            song_playlist: playlist.documentId,
            virtuous_song: docId,
            orderNo: items.length + 1,
            recordOwner: user?.username,
            recordEditor: user?.username
          }
        }
      });

      await load();
    } catch (e) {
      showToast("Failed to add");
    }
  }

  // =========================
  // MOVE
  // =========================
  async function move(index, dir) {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;

    const current = items[index];
    setLoadingId(current.documentId);

    const newItems = [...items];
    [newItems[index], newItems[target]] =
      [newItems[target], newItems[index]];

    setItems(newItems);

    try {
      await Promise.all(newItems.map((it, i) =>
        apiFetch(`/api/song-playlist-items/${it.documentId}`, {
          method: "PUT",
          body: { data: { orderNo: i + 1 } }
        })
      ));

      onSaved();
    } catch (e) {
      showToast("Failed reorder");
    }

    setLoadingId(null);
  }

  // =========================
  // REMOVE
  // =========================
  async function remove(item) {
    if (!window.confirm("Remove from playlist?")) return;

    setLoadingId(item.documentId);

    setItems(prev => prev.filter(i => i.documentId !== item.documentId));

    try {
      await apiFetch(`/api/song-playlist-items/${item.documentId}`, {
        method: "DELETE"
      });

      onSaved();
      showToast("Removed");
    } catch (e) {
      showToast("Failed remove");
    }

    setLoadingId(null);
  }

  return (
    <>
      {/* LOOKUP */}
      <LookupSong
        value={selectedSong}
        onChange={(item) => setSelectedSong(item)}
        placeholder="Cari lagu..."
      />

      {/* ADD BUTTON */}
      <button
        className="btn-primary"
        style={{ marginTop: "0.5rem", marginBottom: "0.75rem" }}
        disabled={!selectedSong}
        onClick={handleAdd}
      >
        + Add Song
      </button>

      {/* LIST */}
      {items.map((it, idx) => {
        const s = it.virtuous_song;
        const hasLirik = !!(
          s?.JudulLirik?.trim() || 
          s?.JudulLirikPinyin?.trim()
        );

        const hasNada = !!(
          s?.JudulNada?.trim() || 
          s?.JudulNadaPinyin?.trim()
        );

        const isProcessing = loadingId === it.documentId;
        const isTop = idx === 0;
        const isBottom = idx === items.length - 1;

        return (
          <div key={it.documentId} style={rowStyle} className="content-row">

            <div style={{ flex: 1, minWidth: 0 }}>

            {hasLirik ? (
              <>
                {/* PRIMARY: LIRIK */}
                <div style={{ fontWeight: 600 }}>
                  <span style={{ color: "#666", fontWeight: 400 }}>
                    Lirik (Yujì)
                  </span>{" "}
                  {s?.JudulLirik} {s?.JudulLirikPinyin}
                </div>

                {hasNada && (
                  <>
                  {/* SECONDARY: NADA */}
                  <div style={{ fontSize: "0.75rem", color: "#666" }}>
                    <span style={{ fontWeight: 500 }}>
                      Nada (Diaoji)
                    </span>{" "}
                    {s?.JudulNada} {s?.JudulNadaPinyin}
                  </div>
                  </>
                )}
              </>
            ) : (
              /* 👉 kalau tidak ada lirik, jadikan NADA sebagai primary */
              <div style={{ fontWeight: 600 }}>
                <span style={{ color: "#666", fontWeight: 400 }}>
                  Nada (Diaoji)
                </span>{" "}
                {s?.JudulNada} {s?.JudulNadaPinyin}
              </div>
            )}

            </div>

            <div style={{ display: "flex", gap: "0.25rem" }}>

              <button
                className="btn-secondary"
                style={btnSmall}
                disabled={isTop || isProcessing}
                onClick={() => move(idx, -1)}
              >
                {isProcessing ? "..." : "↑"}
              </button>

              <button
                className="btn-secondary"
                style={btnSmall}
                disabled={isBottom || isProcessing}
                onClick={() => move(idx, 1)}
              >
                {isProcessing ? "..." : "↓"}
              </button>

              <button
                className="btn-danger"
                style={btnSmall}
                disabled={isProcessing}
                onClick={() => remove(it)}
              >
                {isProcessing ? "..." : "✕"}
              </button>

            </div>

          </div>
        );
      })}

      {/* TOAST */}
      {toast && (
        <div style={{
          position: "fixed",
          bottom: "1rem",
          right: "1rem",
          background: "#333",
          color: "#fff",
          padding: "0.5rem 0.75rem",
          borderRadius: "0.5rem",
          fontSize: "0.75rem"
        }}>
          {toast}
        </div>
      )}
    </>
  );
}